import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { User, KeyRound, Settings as SettingsIcon, Ticket, Share2, Mail, MessageCircle, Hash, Phone } from 'lucide-react';
import CouponDialog from './CouponDialog';
import InviteDialog from './InviteDialog';
import BindEmailDialog from './BindEmailDialog';
import NotificationBell from './NotificationBell';
import PointBadge from './PointBadge';
import api from '../api/axios';

export type ProfileTraffic = 'green' | 'yellow' | 'red';
export interface ProfileStatus {
  state: ProfileTraffic;
  missing: string[];
}

interface Props {
  profileStatus: ProfileStatus;
  onClickChangePassword?: () => void;
  onClickOpenCoupons?: () => void; // 互換維持
}

type Provider = 'email' | 'wechat' | 'phone' | 'line' | 'unknown';

const MypageNavbar: React.FC<Props> = ({ profileStatus, onClickChangePassword, onClickOpenCoupons }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  // ===== 基本情報（信号色・ツールチップ） =====
  const statusColor =
    profileStatus.state === 'green'
      ? 'bg-emerald-500'
      : profileStatus.state === 'yellow'
        ? 'bg-amber-500'
        : 'bg-red-500';

  const statusLabel =
    profileStatus.state === 'green'
      ? (t('mypage.profile_ok') || '基本情報：入力済み')
      : profileStatus.state === 'yellow'
        ? (t('mypage.profile_partial') || '基本情報：一部未入力')
        : (t('mypage.profile_missing') || '基本情報：未入力');

  const tooltipText =
    (statusLabel +
      (profileStatus.missing.length
        ? `（${t('mypage.missing') || '不足'}: ${profileStatus.missing.join('、')}）`
        : '')) || '';

  // ===== 歯車メニューの開閉（外側クリックで閉じる） =====
  const [openMenu, setOpenMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (!menuRef.current) return;
      if (!menuRef.current.contains(e.target as Node)) {
        setOpenMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ===== クーポン／友達紹介／メール連携ダイアログ =====
  const [showCouponDlg, setShowCouponDlg] = useState(false);
  const [showInviteDlg, setShowInviteDlg] = useState(false);
  const [showBindEmailDlg, setShowBindEmailDlg] = useState(false);
  const [isEmployee, setIsEmployee] = useState(false);
  const [companyName, setCompanyName] = useState<string>('');
  const [memberType, setMemberType] = useState<string>('');
  const handleOpenCoupons = () => {
    setOpenMenu(false);
    if (onClickOpenCoupons) onClickOpenCoupons();
    else setShowCouponDlg(true);
  };
  const handleOpenInvite = () => {
    setOpenMenu(false);
    setShowInviteDlg(true);
  };

  // ===== 認証情報（/auth/me から取得） =====
  const [provider, setProvider] = useState<Provider>('unknown');
  const [hasEmail, setHasEmail] = useState<boolean>(false);
  const [displayName, setDisplayName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  // ★ 新規：カルテ番号（病历号）
  const [chartNumber, setChartNumber] = useState<string>('');
  const hasChartNumber = !!chartNumber;

  useEffect(() => {
    (async () => {
      try {
        // /auth/me は display_name / provider / email / chart_number を返す
        const res = await api.get('/auth/me');
        const data = res.data || {};

        const dn =
          (data?.display_name && String(data.display_name)) ||
          (data?.name && String(data.name)) ||
          (data?.nickname && String(data.nickname)) ||
          '';
        setDisplayName(dn);

        const em = (data?.email && String(data.email)) || '';
        setEmail(em);
        setHasEmail(!!em);

        // ここからカルテ番号
        const cn = (data?.chart_number && String(data.chart_number)) || '';
        setChartNumber(cn);

        // プロバイダー判定
        let prov: Provider = 'unknown';
        if (data?.provider === 'wechat') prov = 'wechat';
        else if (data?.provider === 'phone') prov = 'phone';
        else if (data?.provider === 'line') prov = 'line';
        else if (data?.provider === 'email') prov = 'email';
        else if (em) prov = 'email';
        setProvider(prov);

        // 社員かどうか確認
        try {
          const companyRes = await api.get('/user/company');
          const mt = companyRes.data?.member_type || '';
          setMemberType(mt);
          setIsEmployee(mt === 'employee');
          setCompanyName(companyRes.data?.company?.name || '');
        } catch {
          // 法人未所属なら無視
        }
      } catch {
        // 未ログインなどは無視
      }
    })();
  }, []);

  // メールユーザーと電話ユーザーのみ「パスワード変更」を表示（WeChat/LINE は非表示）
  const showChangePassword = (provider === 'email' && hasEmail) || provider === 'phone';
  // メール未連携ユーザー（WeChat/LINE）に「メール連携」を表示
  const showBindEmail = !hasEmail && (provider === 'wechat' || provider === 'line' || provider === 'unknown');

  return (
    <>
      <nav className="bg-gray-100 border-b border-gray-200 py-2 lg:py-3 rounded-t-[2rem] sm:rounded-t-[2.5rem]">
        <div className="flex items-center justify-between w-full gap-2 px-6">
          {/* 左側タブ */}
          <div className="flex items-center gap-1.5 sm:gap-6 flex-nowrap overflow-x-auto scrollbar-hide pr-2 min-w-0">
            {[
              { path: '/mypage', label: t('mypage.top'), short: 'TOP' },
              { path: '/mypage/records', label: t('mypage.reservations') },
              { path: '/mypage/extra', label: t('mypage.extra_delivery') },
            ].map((item) => (
              <button
                key={item.path}
                onClick={() => navigate(item.path)}
                className={classNames(
                  'relative whitespace-nowrap hover:text-coral',
                  'text-sm md:text-base px-1.5 py-0.5',
                  location.pathname === item.path && 'text-coral font-semibold border-b-2 border-coral'
                )}
              >
                {item.short ? (
                  <>
                    <span className="sm:hidden">{item.short}</span>
                    <span className="hidden sm:inline">{item.label}</span>
                  </>
                ) : (
                  item.label
                )}

              </button>
            ))}
          </div>

          {/* 右側：基本情報 + 歯車メニュー */}
          <div className="flex items-center gap-2 sm:gap-3 shrink-0 min-w-0">
            {/* 基本情報ボタン（プロフィール） */}
            <button
              onClick={() => navigate('/mypage/profile')}
              className="relative group flex items-center gap-1 hover:text-coral transition whitespace-nowrap"
              title={tooltipText}
              aria-label={t('mypage.profile') || '基本情報'}
            >
              <User className="w-5 h-5" />
              <span className="text-sm hidden sm:inline">{t('mypage.profile')}</span>
              <span className={classNames('ml-1 inline-block w-2.5 h-2.5 rounded-full ring-2 ring-white', statusColor)} />
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-1 pointer-events-none opacity-0 group-hover:opacity-100 transition text-xs text-white bg-gray-800 px-2 py-1 rounded whitespace-nowrap shadow">
                {tooltipText}
              </span>
            </button>

            {/* ポイント残高バッジ */}
            <PointBadge />

            {/* 通知ベル */}
            <NotificationBell />

            {/* 歯車メニュー */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setOpenMenu((v) => !v)}
                className="flex items-center gap-1 text-sm p-1.5 rounded-md hover:bg-gray-200 transition-colors whitespace-nowrap"
                title={t('settings.title') || '設定'}
                aria-haspopup="menu"
                aria-expanded={openMenu}
              >
                <SettingsIcon className="w-5 h-5 text-gray-700 hover:text-coral transition-colors" />
              </button>

              {openMenu && (
                <div
                  role="menu"
                  className="absolute right-0 mt-2 w-auto min-w-[12rem] max-w-[17rem] bg-white rounded-xl shadow-lg border overflow-hidden z-20"
                >
                  {/* 上部：ユーザー名＋ログイン方式バッジ */}
                  <div className="px-3 py-3 border-b bg-gray-50">
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        {/* 1行目：名前 */}
                        <div className="font-semibold truncate" title={displayName || undefined}>
                          {displayName || t('auth_status.unknown_user') || 'ユーザー'}
                        </div>

                        {/* 2行目：カルテ番号（あれば常に表示） */}
                        {hasChartNumber && (
                          <div
                            className="text-xs text-gray-600 truncate mt-0.5 inline-flex items-center gap-1"
                            title={chartNumber}
                          >
                            <Hash className="w-3.5 h-3.5" />
                            <span className="shrink-0">{t('auth_status.chart_number_label') || 'カルテ番号'}</span>
                            <span className="truncate">{chartNumber}</span>
                          </div>
                        )}

                        {/* 3行目：法人所属バッジ（member_typeで表示分岐） */}
                        {companyName && memberType && (
                          <div className="mt-1.5">
                            <span className="inline-block px-2.5 py-0.5 text-[11px] font-semibold rounded-full bg-orange-100 text-orange-700 ring-1 ring-orange-300 whitespace-nowrap">
                              {memberType === 'employee' ? companyName
                                : memberType === 'family' ? t('company.member_family', 'ご家族会員')
                                : t('company.member_friend', 'ご友人会員')}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* バッジ：WeChat / LINE / Email / Phone */}
                      {provider === 'wechat' ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200 shrink-0"
                          title={t('auth_status.provider_wechat') || 'WeChatログイン'}
                        >
                          <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                          <MessageCircle className="w-3.5 h-3.5" />
                          {t('auth_status.wechat_badge') || 'WeChat'}
                        </span>
                      ) : provider === 'line' ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full bg-[#06c755]/10 text-[#06c755] border border-[#06c755]/30 shrink-0"
                          title="LINEログイン"
                        >
                          <span className="inline-block w-2 h-2 rounded-full bg-[#06c755]" />
                          LINE
                        </span>
                      ) : provider === 'phone' ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200 shrink-0"
                          title={t('auth_status.provider_phone') || '電話番号ログイン'}
                        >
                          <Phone className="w-3.5 h-3.5" />
                          {t('auth_status.phone_badge') || 'Phone'}
                        </span>
                      ) : provider === 'email' ? (
                        <span
                          className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] rounded-full bg-coral-light text-coral border border-coral/20 shrink-0"
                          title={t('auth_status.provider_email') || 'メールログイン'}
                        >
                          <Mail className="w-3.5 h-3.5" />
                          {t('auth_status.email_badge') || 'Email'}
                        </span>
                      ) : null}
                    </div>
                  </div>

                  {/* メニュー項目 */}
                  <div className="py-1">
                    {/* メール未連携ユーザー：メール連携エントリ */}
                    {showBindEmail && (
                      <button
                        role="menuitem"
                        onClick={() => {
                          setOpenMenu(false);
                          setShowBindEmailDlg(true);
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50 text-coral"
                      >
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{t('login_success.bind_entry')}</span>
                      </button>
                    )}

                    {/* メールユーザーのみ：パスワード変更 */}
                    {showChangePassword && (
                      <button
                        role="menuitem"
                        onClick={() => {
                          setOpenMenu(false);
                          onClickChangePassword?.();
                        }}
                        className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                      >
                        <KeyRound className="w-4 h-4" />
                        <span className="truncate">{t('password.change')}</span>
                      </button>
                    )}

                    <button
                      role="menuitem"
                      onClick={handleOpenCoupons}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                    >
                      <Ticket className="w-4 h-4" />
                      <span className="truncate">{t('mypage.view_coupons') || 'クーポン'}</span>
                    </button>

                    <button
                      role="menuitem"
                      onClick={handleOpenInvite}
                      className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-gray-50"
                    >
                      <Share2 className="w-4 h-4" />
                      <span className="truncate">
                        {isEmployee ? (t('invite.open_employee') || '社員紹介') : (t('invite.open') || '友達紹介')}
                      </span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* 内部管理のダイアログ描画 */}
      <CouponDialog open={showCouponDlg} onClose={() => setShowCouponDlg(false)} />
      <InviteDialog open={showInviteDlg} onClose={() => setShowInviteDlg(false)} isEmployee={isEmployee} />
      <BindEmailDialog
        open={showBindEmailDlg}
        onClose={() => setShowBindEmailDlg(false)}
        onSuccess={() => {
          setHasEmail(true);
          setProvider('email');
        }}
      />
    </>
  );
};

export default MypageNavbar;
